import classNames from 'classnames';
import { render, unmount } from 'rc-util/lib/React/render';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import CommonModal from '@/ComUI/commonModal';
import message from '@/ComUI/message';
import { useConfig } from '@/configProvider';
import { useLocaleStore } from '@/i18n';
import { IResponseTeamCreate } from '@/types';
import { changeIdentity } from '..';
import Button from '../../ComUI/button';
import { TypeTrack } from '../identitySwitch/shared';
import { createTeam } from '../utils';
import './index.less';

export interface CreateTeamModalProps {
    /** 创建团队完后, 是否切换身份, 默认为切换 */
    noSwitchIdentity?: boolean;
    /** 创建团队成功的entrance埋点参数 */
    create_team_success_entrance?: string;
    // 来自哪个具体的项目
    project?: string;
    onCreatedComplete?: (data: IResponseTeamCreate) => void;
    onClose?: () => void;
    onTrack?: TypeTrack;
}

interface CreateTeamModalComponent extends React.FC<CreateTeamModalProps> {
    open: (props: CreateTeamModalProps) => void;
}

const CreateTeamModal: CreateTeamModalComponent = ({
    project = '',
    noSwitchIdentity,
    create_team_success_entrance,
    onCreatedComplete,
    onClose,
    onTrack,
}) => {
    const { $t, locale } = useLocaleStore();
    const { userInfo } = useConfig();
    const inputRef = useRef<any>();

    const [teamName, setTeamName] = useState('');
    const isPending = useRef(false);

    const BannerImage = useMemo(() => {
        if (locale === 'en') {
            return 'https://xiuxiu-pro-new.meitudata.com/poster/505252b96d40d03b33529c242b918bb0.png';
        }
        return 'https://public.static.meitudata.com/xiuxiu-pc/xdesign-widgets/images/teamInfo/create-modal-banner.png';
    }, [locale]);

    const maxTeamNameLength = 21;

    useEffect(() => {
        setTeamName('');
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!userInfo.screen_name) return;
        setTeamName($t((v, { r }) => r(v.Text_DefaultTeamName, [userInfo.screen_name])));
    }, [userInfo.screen_name]);

    console.log(create_team_success_entrance, 'CreateTeamModal======', project);

    const createHandle = () => {
        if (!teamName) {
            return message.error($t((v) => v.Message_FillTeamInfo));
        }
        if (teamName.length >= maxTeamNameLength) {
            return message.error($t((v) => v.Message_TeamNameMaxLength));
        }
        if (isPending.current) return;
        isPending.current = true;

        createTeam({
            teamName,
        })
            .then((res) => {
                onTrack?.('create_team_success', {
                    entrance: create_team_success_entrance ?? 'create_team_1',
                    project,
                    team_name: teamName,
                    newcreate_oid: res.oid,
                    newcreate_uid: res.ouid,
                });
                onCreatedComplete?.({
                    group_id: res.groupId,
                    oid: res.oid,
                    ouid: res.ouid,
                    team_name: res.teamName,
                });
                message.success($t((v) => v.Tips_CreateSuccess));
                // 会员弹窗关闭时回调函数
                onClose?.();
                if (!noSwitchIdentity) {
                    changeIdentity({
                        ouid: res.ouid,
                        oid: res.oid,
                        groupId: res.groupId,
                    });
                }
            })
            .finally(() => {
                isPending.current = false;
                console.log('create team finally');
            });
    };

    return (
        <CommonModal open destroyOnClose className="Widget teamSpaceCreateModal">
            <div className="teamSpaceCreateModal-inner">
                <div className="close" onClick={onClose}>
                    <IconFont fontSize={14} name="CrossBlack" color="var(--content-system-tertiary)" />
                </div>

                <div className="teamSpaceCreateModal-banner">
                    <img src={BannerImage} alt="" />
                </div>

                <div className="teamSpaceCreateModal-form">
                    <div className="teamSpaceCreateModal-formTitle">{$t((v) => v.Title_CreateTeam)}</div>
                    <div className="teamSpaceCreateModal-formLabel">{$t((v) => v.Label_TeamName)}</div>
                    <div
                        className={classNames('teamSpaceCreateModal-input', {
                            warn: teamName.length >= maxTeamNameLength,
                        })}
                    >
                        <input
                            value={teamName}
                            ref={inputRef}
                            placeholder={$t((v) => v.Placeholder_TeamName)}
                            type="text"
                            maxLength={maxTeamNameLength}
                            onChange={(e: any) => {
                                setTeamName(e.target.value);
                            }}
                        />
                    </div>
                    <Button
                        type="primary"
                        style={{ marginTop: 32, height: 48, fontSize: 16 }}
                        onClick={() => {
                            createHandle();
                        }}
                    >
                        {$t((v) => v.Button_FreeCreate)}
                    </Button>
                    <p className="teamSpaceCreateModal-renameTip">{$t((v) => v.Tip_TeamNameEditable)}</p>
                </div>
            </div>
        </CommonModal>
    );
};

CreateTeamModal.displayName = 'CreateTeamModal';

let el: HTMLDivElement | null = null;

CreateTeamModal.open = ({ onClose, ...restProps }: CreateTeamModalProps) => {
    if (!el) {
        el = document.createElement('div');
    }
    document.body.appendChild(el);

    render(
        <CreateTeamModal
            {...restProps}
            onClose={() => {
                if (el && el.parentNode) {
                    unmount(el);
                    el.parentNode.removeChild(el);
                    el = null;
                }
                onClose?.();
            }}
        />,
        el,
    );
};

export const openCreateTeamModal = CreateTeamModal.open;
